import { CloseCircleOutlined } from '@ant-design/icons';
import styled from 'styled-components';

export const MultipleDropDownWrapper = styled.div`
  position: relative;

  .styledLabel {
    margin: ${({ theme }): string => theme.spacers!.spacer02} 0;
    font-size: 14px;
    font-weight: 500 !important;
    color: ${({ theme }): string => theme.admanColors!.gray.light.fg[2]};
  }
`;

export const StyledInputWrapper = styled.div<{ $focused: boolean; $errMsg?: boolean }>`
  width: 100%;
  position: relative;

  input {
    padding: ${({ theme }): string => `${theme.spacers!.spacer02} ${theme.spacers!.spacer03}`};
    width: 100%;
    height: 40px;
    text-overflow: ellipsis;
    border: 1px solid
      ${({ theme, $errMsg }): string => ($errMsg ? theme.admanColors!.red.solid : theme.admanColors!.gray.light.fg[5])};
    border-radius: 4px;
    padding-right: ${({ theme }): string => `${theme.spacers!.spacer09}`};

    &:focus-visible {
      outline-color: ${({ theme }): string => theme.admanColors!.gray.light.fg[0]};
    }

    &:disabled {
      background-color: ${({ theme }): string => theme.admanColors!.gray.light.bg2[7]};
      color: ${({ theme }): string => theme.admanColors!.gray.dark.fg[6]};
      border: 1px solid ${({ theme }): string => theme.admanColors!.gray.dark.fg[6]};
    }

    &:hover:not(:focus-visible) {
      box-shadow: inset 0 0 0 1px ${({ theme }): string => theme.admanColors!.gray.dark.fg[6]};
    }

    &::placeholder {
      color: ${({ theme }): string => theme.admanColors!.gray.light.fg[2]};
      font-family: Graphik;
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: 160%;
      letter-spacing: 0.14px;
    }
  }

  svg {
    position: absolute;
    top: 16px;
    right: 12px;
    transform: rotate(${({ $focused }): string => ($focused ? '180' : '0')}deg);
    transition: 0.2s;
  }

  div.transparent {
    position: absolute;
    top: 6px;
    right: 35px;
    width: 24px;
  }
`;

export const StyledErrorMessage = styled.div`
  color: ${({ theme }): string => theme.admanColors!.red.solid};
  font-size: 12px;
  line-height: 18px;
  padding: ${({ theme }): string => theme.spacers!.spacer01};
`;

export const StyledOptionWrapper = styled.div<{ $openOnTop?: boolean }>`
  margin-top: ${({ theme }): string => theme.spacers!.spacer01};
  overflow: hidden;
  width: 100%;
  background-color: ${({ theme }): string => theme.admanColors!.gray.light.bg0[0]};
  position: absolute;
  padding: ${({ theme }): string => theme.spacers!.spacer01};
  display: grid;
  border: 1px solid ${({ theme }): string => theme.admanColors!.gray.light.bg0[3]};
  box-shadow: 0 4px 16px -8px rgb(19 32 57 / 40%);
  border-radius: 12px;
  z-index: 10;
  bottom: ${({ $openOnTop }): string => ($openOnTop ? '45px' : '')};

  &:hover {
    color: ${({ theme }): string => theme.admanColors!.blue.solid};
  }

  > div {
    &::-webkit-scrollbar,
    &::-webkit-scrollbar-track-piece,
    &::-webkit-scrollbar-thumb {
      width: 4px;
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background: ${({ theme }): string => theme.admanColors!.gray.light.fg[0]};
    }
  }
`;

export const StyledWrapperOption = styled.div<{ $checked?: boolean }>`
  display: flex;
  border-radius: 8px;
  user-select: none;
  background-color: ${({ theme, $checked }): string => ($checked ? theme.admanColors!.blue.light.bg[1] : 'inherit')};
  color: ${({ theme, $checked }): string =>
    $checked ? theme.admanColors!.gray.light.fg[0] : theme.admanColors!.gray.light.fg[1]};

  &:hover {
    background-color: ${({ theme, $checked }): string =>
      $checked ? theme.admanColors!.blue.light.bg[1] : theme.admanColors!.blue.light.bg1[8]};
  }
`;

export const StyledOption = styled.label<{
  withoutSelectIcon?: boolean;
  $checkedValues?: boolean[];
  $disabled?: boolean;
}>`
  span {
    font-family: InspireTWDC;
    font-size: 14px;
  }

  position: relative;
  height: ${({ withoutSelectIcon }): string => (withoutSelectIcon ? 'inherit' : '40px')};
  padding: 0;
  font-size: 14px;
  width: 100%;
  display: flex;
  align-items: center;
  font-style: normal;
  font-weight: 400;
  line-height: 12px;
  letter-spacing: 0.5px;

  color: ${({ theme }): string => theme.admanColors!.gray.light.fg[0]};
  background-color: ${({ theme, $checkedValues = [], $disabled }): string => {
    const [, currentValue] = $checkedValues;

    if (!currentValue) {
      return 'inherit';
    }

    if ($disabled) {
      return theme.admanColors!.gray.light.bg2[7];
    }

    return theme.admanColors!.blue.light.bg[1];
  }};
  border-radius: ${({ withoutSelectIcon, $checkedValues = [] }): string => {
    const [prevValue, currentValue, nextValue] = $checkedValues;
    const top = prevValue && currentValue ? '0 0' : '8px 8px';
    const bottom = nextValue && currentValue ? '0 0' : '8px 8px';

    return withoutSelectIcon ? `${top} ${bottom}` : 'inherit';
  }};

  &:hover {
    background-color: ${({ theme, $checkedValues = [], $disabled }): string => {
      const [, currentValue] = $checkedValues;

      if (!currentValue) {
        return theme.admanColors!.blue.light.bg1[8];
      }

      if ($disabled) {
        return theme.admanColors!.gray.light.bg2[7];
      }

      return theme.admanColors!.blue.light.bg[1];
    }};
  }

  svg {
    margin-left: ${({ theme }): string => theme.spacers!.spacer03};
    cursor: pointer;
  }
`;

export const StyledCheckboxInputWrapper = styled.div<{ $isSelected: boolean }>`
  margin-left: ${({ theme }): string => theme.spacers!.spacer03};
`;

export const StyledCheckedIconWrapper = styled.div`
  margin-left: ${({ theme }): string => theme.spacers!.spacer04};

  svg {
    position: absolute;
    top: calc(50% - 5px);
    left: 0;
  }
`;

export const StyledClearButton = styled(CloseCircleOutlined)`
  display: flex;

  svg {
    top: 12px;
    right: 39px;
    transform: none;
  }
`;
